import WorkshopFilters from '../features/order-list/WorkshopFilters'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { isInvalidDateRange } from '../shared/lib/time'
import DiagnosticsList from '../features/order-list/DiagnosticsList'
import { exportDiagnostics } from '../shared/lib/exportDiagnostics'
import { filterRosOrders, summarizeRos } from '../features/order-list/rosDashboard'
import {INTEREST_METRICS} from '../features/order-list/interestMetrics'
import { buildWorkshopIntervals, workshopBarHeight, workshopPeriodDates } from '../features/order-list/workshopIntervals'
import './Ros.css'

const metricConfig = [
    { key: 'all', label: 'Все', bg: '#0052FF', icon: 'ti-list', area: 'all' },
    { key: 'in_progress', label: 'В работе', bg: '#F59E0B', icon: 'ti-tool', area: 'prog' },
    { key: 'done', label: 'Завершено', bg: '#10B981', icon: 'ti-check', area: 'done' },
    { key: 'expired', label: 'Просрочено', bg: '#DC2626', icon: 'ti-alert-triangle', area: 'exp' },
]

function DonutChart({ value, max, color, trackColor, size = 80, strokeWidth = 10 }) {
    const r = (size - strokeWidth) / 2
    const circ = 2 * Math.PI * r
    const pct = value != null && max > 0 ? Math.min(value / max, 1) : 0
    const dash = pct * circ
    const offset = circ / 4
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={strokeWidth} />
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
                strokeDasharray={`${dash} ${circ}`} strokeDashoffset={offset} strokeLinecap="round" />
            <text x={size / 2} y={size / 2 - 5} textAnchor="middle" fontSize="14" fontWeight="700" fill="#1A1A1A" fontFamily="-apple-system,sans-serif">{value ?? '—'}</text>
            <text x={size / 2} y={size / 2 + 9} textAnchor="middle" fontSize="9" fill="#9CA3AF" fontFamily="-apple-system,sans-serif">мин</text>
        </svg>
    )
}

export default function Ros() {
    const navigate = useNavigate()
    const [orders, setOrders] = useState([])
    const [loadError, setLoadError] = useState('')
    const request = useRef(null)
    const [loading, setLoading] = useState(true)
    const [filterStatus, setFilterStatus] = useState([])
    const [filterMechanic, setFilterMechanic] = useState([])
    const [filterMaster, setFilterMaster] = useState([])
    const [filterBrand, setFilterBrand] = useState([])
    const [vibeMin, setVibeMin] = useState('')
    const [vibeMax, setVibeMax] = useState('')
    const [filterFrom, setFilterFrom] = useState('')
    const [filterTo, setFilterTo] = useState('')
    const [period, setPeriod] = useState('week')
    const [staffTab, setStaffTab] = useState('mechanic')
    const [search, setSearch] = useState('')
    const [exporting, setExporting] = useState(false)
    const [vibeMetric, setVibeMetric] = useState('vibe')
    const [vibeDropdown, setVibeDropdown] = useState(false)
    const { token } = useAuth()

    const loadData = useCallback(() => {
        request.current?.abort()
        if (period === 'custom' && isInvalidDateRange(filterFrom, filterTo)) {
            setLoading(false)
            return
        }
        const controller = new AbortController()
        request.current = controller
        const { from, to } = workshopPeriodDates(period, filterFrom, filterTo)
        // This page's seven-day week includes today; do not let the legacy API expand it to eight days.
        const params = new URLSearchParams({ period: period === 'week' ? 'custom' : period })
        if (from) params.set('from', from)
        if (to) params.set('to', to)
        setLoading(true)
        setLoadError('')
        fetch(`/api/orders?${params}`, { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } })
            .then(async response => {
                if (!response.ok) throw new Error('Не удалось загрузить заявки. Повторите попытку.')
                const data = await response.json()
                if (!Array.isArray(data)) throw new Error('Не удалось загрузить заявки. Повторите попытку.')
                if (!controller.signal.aborted) setOrders(data)
            }).catch(error => {
                if (!controller.signal.aborted) setLoadError(error.message)
            }).finally(() => { if (!controller.signal.aborted) setLoading(false) })
    }, [token, period, filterFrom, filterTo])

    useEffect(() => { loadData(); return () => request.current?.abort() }, [loadData])

    const invalidPeriod = period === 'custom' && isInvalidDateRange(filterFrom, filterTo)
    const filteredOrders = useMemo(() => loading || loadError || invalidPeriod ? [] : filterRosOrders(orders, {
        search, statuses: filterStatus, mechanics: filterMechanic, masters: filterMaster,
        brands: filterBrand, min: vibeMin, max: vibeMax,
    }), [orders, loading, loadError, invalidPeriod, search, filterStatus, filterMechanic, filterMaster, filterBrand, vibeMin, vibeMax])
    // Bounds use the full accessible period, not the search result, so empty intervals stay in place.
    const intervals = useMemo(() => loading || loadError || invalidPeriod ? [] : buildWorkshopIntervals({
        period, from: filterFrom, to: filterTo, orders,
    }), [orders, loading, loadError, invalidPeriod, period, filterFrom, filterTo])
    const stats = useMemo(() => summarizeRos(filteredOrders, intervals), [filteredOrders, intervals])
    const matchingStats = stats
    const vibeStats = stats.vibe

    useEffect(() => {
        if (!token) return
        let socket
        let reconnectTimeout
        let isUnmounted = false

        const connect = () => {
            const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
            socket = new WebSocket(`${protocol}://${window.location.host}/api/ws?token=${token}`)
            socket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data)
                    if (data.type === 'orders_changed') loadData()
                } catch { }
            }
            socket.onclose = () => {
                if (isUnmounted) return
                reconnectTimeout = setTimeout(connect, 5000)
            }
        }

        connect()

        return () => {
            isUnmounted = true
            clearTimeout(reconnectTimeout)
            if (socket) socket.close()
        }
    }, [token, loadData])

    const vibeMetrics = INTEREST_METRICS.map(metric=>({key:metric.legacyKey,label:metric.label,color:metric.color,suffix:metric.unit.trim(),isPercent:metric.unit==='%',fixedMax:metric.max}))
    const currentVibeMetric = vibeMetrics.find(m => m.key === vibeMetric)

    useEffect(() => {
        if (!Array.isArray(vibeStats?.dynamics) || !vibeStats.dynamics.length) return
        let chartInstance = null
        let disposed = false

        const loadChart = async () => {
            await new Promise(r => setTimeout(r, 50))
            const canvas = document.getElementById('vibeScoreChart')
            if (!canvas) return
            const { Chart, registerables } = await import('https://cdn.jsdelivr.net/npm/chart.js@4.4.1/+esm')
            if (disposed || !canvas.isConnected) return
            Chart.register(...registerables)
            const existing = Chart.getChart(canvas)
            if (existing) existing.destroy()
            if (chartInstance) chartInstance.destroy()

            const dynamics = vibeStats.dynamics
            const m = currentVibeMetric
            const dataKey = { vibe: 'avg_score', visits: 'visits_pct', scroll: 'scroll_pct', photos: 'photos_pct', actions: 'actions_pct', time: 'avg_time_min' }[vibeMetric]
            const data = dynamics.map(d => d[dataKey])
            const dataMax = Math.max(0, ...data.filter(value => value != null))
            const axisMax = m.fixedMax || Math.max(1, Math.ceil(dataMax * 1.3))

            const labels = dynamics.map(d => d.label)

            const thresholds = vibeMetric === 'vibe'
                ? [{ value: 70, color: '#10B981' }, { value: 30, color: '#F59E0B' }]
                : [{ value: dataMax, color: m.color }]

            chartInstance = new Chart(canvas, {
                type: 'line',
                data: {
                    labels,
                    datasets: [
                        {
                            data,
                            borderColor: m.color,
                            pointBorderColor: m.color,
                            pointBackgroundColor: 'white',
                            borderWidth: 2.5,
                            pointRadius: 4,
                            pointHoverRadius: 5,
                            pointBorderWidth: 2,
                            tension: 0.3,
                            spanGaps: true,
                            order: 1,
                        },
                        ...thresholds.map(t => ({
                            data: Array(labels.length).fill(t.value),
                            borderColor: t.color,
                            borderWidth: 1,
                            borderDash: [4, 3],
                            pointRadius: 0,
                            tension: 0,
                            order: 2,
                        }))
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            mode: 'index',
                            intersect: false,
                            filter: i => i.datasetIndex === 0,
                            callbacks: {
                                title: items => dynamics[items[0]?.dataIndex]?.tooltip || '',
                                label: ctx => ` ${m.label}: ${ctx.parsed.y}${m.suffix}`
                            }
                        }
                    },
                    scales: {
                        x: {
                            display: true,
                            ticks: { font: { size: 9 }, color: '#C4C9D4', autoSkip: true, maxTicksLimit: 12, maxRotation: 45 },
                            grid: { display: false },
                            border: { display: false }
                        },
                        y: {
                            display: true,
                            min: 0,
                            max: axisMax,
                            ticks: {
                                maxTicksLimit: 5,
                                callback: v => `${Math.round(v)}${m.suffix}`,
                                font: { size: 9 },
                                color: '#9CA3AF',
                            },
                            grid: { display: false },
                            border: { display: false }
                        }
                    }
                }
            })
        }
        loadChart()
        return () => { disposed = true; if (chartInstance) chartInstance.destroy() }
    }, [vibeStats, vibeMetric])

    const mechanics = [...new Set(orders.map(o => o.mechanic_name).filter(Boolean))]
    const masters = [...new Set(orders.map(o => o.master_name).filter(Boolean))]
    const brands = [...new Set(orders.map(o => o.car_brand).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'ru'))


    const expiredOpen = matchingStats.expiredOpen
    const expiredProgress = matchingStats.expiredProgress
    const avgTake = stats.avgTakeMinutes
    const avgDiag = stats.avgDiagMinutes
    const maxTake = Math.max(avgTake ?? 0, 60)
    const maxDiag = Math.max(avgDiag ?? 0, 90)

    const mechStats = stats?.mechanicStats || []
    const mechSorted = [...mechStats].filter(m => m.avg_minutes != null).sort((a, b) => a.avg_minutes - b.avg_minutes)
    const fastestMech = mechSorted[0]
    const slowestMech = mechSorted[mechSorted.length - 1]

    const masterStats = stats?.masterStats || []
    const masterSorted = [...masterStats].filter(m => m.avg_minutes != null).sort((a, b) => a.avg_minutes - b.avg_minutes)
    const fastestMaster = masterSorted[0]
    const slowestMaster = masterSorted[masterSorted.length - 1]

    const handleExportXlsx = async () => {
        setExporting(true)
        try {
            await exportDiagnostics({ orders: filteredOrders, token, filters: {
                period,
                from: filterFrom,
                to: filterTo,
                status: filterStatus.join(','),
                mechanic: filterMechanic.join(','),
                master: filterMaster.join(','),
                brand: filterBrand.join(','),
                vibeMin,
                vibeMax,
                search,
            } })
        } catch (e) {
            console.error(e)
        }
        setExporting(false)
    }


    const card = { background: 'white', borderRadius: '18px', padding: '18px', boxShadow: '0 1px 2px rgba(16,24,40,.04),0 4px 14px -6px rgba(16,24,40,.08)' }

    const MetricTiles = () => (
        <>
            {metricConfig.map(m => {
                const value = m.key === 'all' ? matchingStats.total
                    : m.key === 'in_progress' ? matchingStats.inProgress
                        : m.key === 'done' ? matchingStats.sent + matchingStats.closedNotSent
                            : matchingStats.expired
                return (
                    <div key={m.key} className="ros-tile" data-tone={m.key} style={{ gridArea: m.area, background: m.bg }}>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: 'white' }}>{value}</div>
                        {m.key === 'done' && (
                            <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.65)', margin: '2px 0' }}>
                                {matchingStats.sent} SMS · {matchingStats.closedNotSent} Без SMS
                            </div>
                        )}
                        {m.key === 'expired' && (
                            <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.65)', margin: '2px 0' }}>
                                {expiredOpen} пропущено · {expiredProgress} брошено
                            </div>
                        )}
                        <div style={{ fontSize: '11px', fontWeight: '600', color: 'white', opacity: 0.9 }}>{m.label}</div>
                    </div>
                )
            })}
        </>
    )

    const DiagramCards = () => (
        <>
            <div className="ros-donut-card" data-tone="blue">
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#1E40AF', textAlign: 'center' }}>Ср. взятие в работу</div>
                <DonutChart value={avgTake} max={maxTake} color="#0052FF" trackColor="#DBEAFE" />
                <div style={{ fontSize: '10px', color: '#6B7280', textAlign: 'center' }}>от создания до механика</div>
            </div>
            <div className="ros-donut-card" data-tone="green">
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#065F46', textAlign: 'center' }}>Ср. диагностика</div>
                <DonutChart value={avgDiag} max={maxDiag} color="#10B981" trackColor="#D1FAE5" />
                <div style={{ fontSize: '10px', color: '#6B7280', textAlign: 'center' }}>от взятия до завершения</div>
            </div>
        </>
    )

    const StaffRow = ({ m }) => (
        <div className="ros-staff-row" style={{ display: 'grid', gridTemplateColumns: '1fr 36px 36px 36px 56px', gap: '4px', alignItems: 'center', padding: '10px 0', borderTop: '1px solid #F1F3F7' }}>
            <span style={{ fontSize: '13px', color: '#1A1A1A', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</span>
            <span style={{ fontSize: '13px', fontWeight: '700', color: '#1A1A1A', textAlign: 'center' }}>{m.total}</span>
            <span style={{ fontSize: '13px', fontWeight: '700', color: '#10B981', textAlign: 'center' }}>{m.sent || 0}</span>
            <span style={{ fontSize: '13px', fontWeight: '700', color: parseInt(m.expired) > 0 ? '#DC2626' : '#6B7280', textAlign: 'center' }}>{m.expired || 0}</span>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151', textAlign: 'right' }}>{m.avg_minutes ?? '—'} мин</span>
        </div>
    )

    const StaffHeaders = () => (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 36px 36px 36px 56px', gap: '4px', alignItems: 'center', padding: '6px 0' }}>
            <span></span>
            <span style={{ fontSize: '13px', color: '#9CA3AF', textAlign: 'center' }}><i className="ti ti-list" /></span>
            <span style={{ fontSize: '13px', color: '#10B981', textAlign: 'center' }}><i className="ti ti-send" /></span>
            <span style={{ fontSize: '13px', color: '#DC2626', textAlign: 'center' }}><i className="ti ti-alert-triangle" /></span>
            <span style={{ fontSize: '13px', color: '#9CA3AF', textAlign: 'right' }}><i className="ti ti-clock" /></span>
        </div>
    )

    const staffLegend = (
        <p className="ros-icon-legend">
            <span><i className="ti ti-list" style={{ color: '#9CA3AF' }} aria-hidden="true" />всего</span>
            <span><i className="ti ti-send" style={{ color: '#10B981' }} aria-hidden="true" />отправлено</span>
            <span><i className="ti ti-alert-triangle" style={{ color: '#DC2626' }} aria-hidden="true" />просрочено</span>
            <span><i className="ti ti-clock" style={{ color: '#9CA3AF' }} aria-hidden="true" />среднее время</span>
        </p>
    )

    const HeroCards = ({ fastest, slowest }) => (
        fastest && slowest && fastest.name !== slowest.name ? (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <div className="ros-hero" data-tone="fast">
                    <div style={{ fontSize: '10px', fontWeight: '600', color: '#0052FF', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Быстрее всех</div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#1A1A1A', marginBottom: '4px' }}>{fastest.name}</div>
                    <div style={{ fontSize: '22px', fontWeight: '800', color: '#1A1A1A' }}>{fastest.avg_minutes} <span style={{ fontSize: '11px', fontWeight: '500', color: '#9CA3AF' }}>мин</span></div>
                    <div style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '4px' }}>{fastest.sent || 0} отправлено · {fastest.expired || 0} просрочено</div>
                </div>
                <div className="ros-hero" data-tone="slow">
                    <div style={{ fontSize: '10px', fontWeight: '600', color: '#DC2626', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Медленнее всех</div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#1A1A1A', marginBottom: '4px' }}>{slowest.name}</div>
                    <div style={{ fontSize: '22px', fontWeight: '800', color: '#1A1A1A' }}>{slowest.avg_minutes} <span style={{ fontSize: '11px', fontWeight: '500', color: '#9CA3AF' }}>мин</span></div>
                    <div style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '4px' }}>{slowest.sent || 0} отправлено · {slowest.expired || 0} просрочено</div>
                </div>
            </div>
        ) : null
    )

    return (
        <div onClick={() => setVibeDropdown(false)} className="ros-page">

            <WorkshopFilters period={period} setPeriod={setPeriod} from={filterFrom} to={filterTo} setFrom={setFilterFrom} setTo={setFilterTo}
                search={search} setSearch={setSearch} min={vibeMin} max={vibeMax} setMin={setVibeMin} setMax={setVibeMax}
                loading={loading}
                filters={[
                    { key: 'status', label: 'Статус', value: filterStatus, set: setFilterStatus, options: [{ value: 'open', label: 'Новая' }, { value: 'in_progress', label: 'В работе' }, { value: 'closed', label: 'Завершена' }, { value: 'sent', label: 'Отправлена' }, { value: 'expired_open', label: 'Пропущена' }, { value: 'expired_progress', label: 'Брошена' }] },
                    { key: 'mechanic', label: 'Механик', value: filterMechanic, set: setFilterMechanic, options: mechanics.map(value => ({ value, label: value })) },
                    { key: 'master', label: 'Мастер-консультант', value: filterMaster, set: setFilterMaster, options: masters.map(value => ({ value, label: value })) },
                    { key: 'brand', label: 'Марка', value: filterBrand, set: setFilterBrand, options: brands.map(value => ({ value, label: value })) },
                ]}
                onReset={() => { setSearch(''); setFilterStatus([]); setFilterMechanic([]); setFilterMaster([]); setFilterBrand([]); setVibeMin(''); setVibeMax('') }}
            />
            {loading && <p role="status" style={{ margin: 0, fontSize: '13px', color: '#6B7280' }}>Загрузка показателей…</p>}
            {loadError && <div role="alert" style={card}>{loadError} <button type="button" onClick={loadData}>Повторить</button></div>}

            <>
                <style>{`
                            .ros-metric-tiles-grid {
                                display: grid;
                                gap: 10px;
                                grid-template-columns: repeat(2, 1fr);
                                grid-template-areas: "all prog" "done exp";
                            }
                            @media (min-width: 768px) {
                                .ros-metric-tiles-grid {
                                    grid-template-columns: repeat(6, 1fr);
                                    grid-template-areas: "all all all prog prog prog" "done done done exp exp exp";
                                }
                            }
                        `}</style>
                <div className="ros-metric-tiles-grid">
                    <MetricTiles />
                </div>
            </>

            <div className="ros-donuts">
                <DiagramCards />
            </div>

            {vibeStats && (
                <div style={card} className="ros-interest">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <i className="ti ti-flame" style={{ fontSize: '16px', color: '#0052FF' }} />
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#1A1A1A' }}>Интерес клиентов</span>
                        </div>
                        <div style={{ position: 'relative' }}>
                            <div onClick={() => setVibeDropdown(p => !p)} className="ros-select" data-open={vibeDropdown}>
                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: currentVibeMetric.color, flexShrink: 0 }} />
                                <span>{currentVibeMetric.label}</span>
                                <i className={`ti ${vibeDropdown ? 'ti-chevron-up' : 'ti-chevron-down'}`} style={{ fontSize: '11px', color: '#9CA3AF' }} />
                            </div>
                            {vibeDropdown && (
                                <div className="ros-select-menu">
                                    {vibeMetrics.map(m => (
                                        <div key={m.key} onClick={() => { setVibeMetric(m.key); setVibeDropdown(false) }} className="ros-select-option" data-selected={vibeMetric === m.key}>
                                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: m.color, flexShrink: 0 }} />
                                            {m.label}{m.isPercent ? ' %' : ''}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {Array.isArray(vibeStats.dynamics) && vibeStats.dynamics.length > 0 ? (
                        <div style={{ position: 'relative', minWidth: 0, height: `${Math.min(280, 160 + Math.max(0, intervals.length - 12) * 4)}px`, marginBottom: '14px' }}>
                            <canvas id="vibeScoreChart" style={{ display: 'block', position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
                        </div>
                    ) : (
                        <p style={{ color: '#9CA3AF', fontSize: '13px', textAlign: 'center', margin: '20px 0' }}>Нет данных за период</p>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', paddingTop: '14px', borderTop: '1px solid #F1F3F7' }}>
                        <div className="ros-mini-stat">
                            <div style={{ fontSize: '10px', color: '#6B7280', marginBottom: '4px' }}>Ср. открытий</div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#0052FF' }}>{vibeStats.openedPct}<span style={{ fontSize: '11px', fontWeight: '400', color: '#93C5FD' }}>%</span></div>
                        </div>
                        <div className="ros-mini-stat">
                            <div style={{ fontSize: '10px', color: '#6B7280', marginBottom: '4px' }}>Ср. активное время</div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#0052FF' }}>{vibeStats.avgTimeMin}<span style={{ fontSize: '11px', fontWeight: '400', color: '#93C5FD' }}> сек</span></div>
                        </div>
                        <div className="ros-mini-stat">
                            <div style={{ fontSize: '10px', color: '#6B7280', marginBottom: '4px' }}>Нажали связь</div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#0052FF' }}>{vibeStats.actionsPct}<span style={{ fontSize: '11px', fontWeight: '400', color: '#93C5FD' }}>%</span></div>
                        </div>
                        <div className="ros-mini-stat">
                            <div style={{ fontSize: '10px', color: '#6B7280', marginBottom: '4px' }}>Средний индекс интереса</div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: vibeStats.avgScore >= 70 ? 'rgb(16, 185, 129)' : vibeStats.avgScore >= 30 ? 'rgb(255, 116, 56)' : 'rgb(220, 38, 38)' }}>{vibeStats.avgScore}</div>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ ...card, minWidth: 0 }} className="ros-dynamics">
                <p style={{ margin: '0 0 14px', fontSize: '14px', fontWeight: '600', color: '#1A1A1A' }}>Динамика заявок</p>
                {stats.dynamics.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '5px', minWidth: intervals.length > 10 ? `${intervals.length * 48}px` : 0, height: '120px' }}>
                            {stats.dynamics.map((d, i) => {
                                const maximum = Math.max(...stats.dynamics.map(point => point.count))
                                const isMax = d.count > 0 && d.count === maximum
                                return (
                                    <div key={i} className="ros-count-interval" tabIndex={0} title={`${d.tooltip}. Количество: ${d.count}`} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                        <span style={{ fontSize: '10px', fontWeight: '600', color: isMax ? 'white' : '#9CA3AF', background: isMax ? '#0052FF' : 'transparent', borderRadius: '4px', padding: isMax ? '1px 5px' : '0' }}>{d.count}</span>
                                        <div data-count={d.count} className="ros-bar" data-state={isMax ? 'max' : d.count > 0 ? 'some' : 'zero'} style={{ width: '100%', maxWidth: '40px', height: `${workshopBarHeight(d.count, maximum)}px` }} />
                                        <span style={{ fontSize: '10px', color: '#9CA3AF', textAlign: 'center', minHeight: '28px', overflowWrap: 'anywhere' }}>{d.label}</span>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                ) : <p style={{ color: '#9CA3AF', fontSize: '13px', textAlign: 'center', margin: '20px 0' }}>Нет данных за период</p>}
            </div>

            <div style={card} className="ros-staff">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1A1A1A' }}>Статистика по сотрудникам</p>
                    <div className="ros-switch">
                        <button onClick={() => setStaffTab('mechanic')} aria-pressed={staffTab === 'mechanic'}>Механики</button>
                        <button onClick={() => setStaffTab('master')} aria-pressed={staffTab === 'master'}>Мастера</button>
                    </div>
                </div>
                {staffTab === 'mechanic' && (
                    mechStats.length > 0 ? (
                        <>
                            <HeroCards fastest={fastestMech} slowest={slowestMech} />
                            <StaffHeaders />
                            {mechStats.map((m, i) => <StaffRow key={i} m={m} />)}
                            {staffLegend}
                        </>
                    ) : <p style={{ color: '#9CA3AF', fontSize: '13px', textAlign: 'center', margin: '20px 0' }}>Нет данных за период</p>
                )}
                {staffTab === 'master' && (
                    masterStats.length > 0 ? (
                        <>
                            <HeroCards fastest={fastestMaster} slowest={slowestMaster} />
                            <StaffHeaders />
                            {masterStats.map((m, i) => <StaffRow key={i} m={m} />)}
                            {staffLegend}
                        </>
                    ) : <p style={{ color: '#9CA3AF', fontSize: '13px', textAlign: 'center', margin: '20px 0' }}>Нет данных за период</p>
                )}
            </div>

            <DiagnosticsList orders={filteredOrders} onOrder={order => navigate(`/orders/${order.id}`)} onExport={handleExportXlsx} exporting={exporting} />

        </div>


    )
}
